<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import { Button, Title, Text, Image } from '@lib/components';
  import { slide } from '@lib/directives';
  import type { CallToAction } from '@lib/types/survey';
  import type { QuestionHeaderProps } from '@lib/components/survey/question/QuestionHeader/types';
  import type { ScreenConfig } from './types';
  import type { ElementBlock } from '@lib/components/elements';
  import ScreenBlock from './ScreenBlock.svelte';

  export let header: QuestionHeaderProps | undefined = undefined;
  export let cta: CallToAction | undefined = undefined;
  export let config: ScreenConfig | undefined = undefined;
  export let blocks: ElementBlock[] | undefined = undefined;

  const dispatch = createEventDispatcher<{ click: void; autoSubmit: void; getGeolocation: void; requestFullscreen: void }>();
  let visible = true;
  let hasAutoSubmitted = false;
  let showContent = true;
  let backgroundEl: HTMLElement;

  function getThemeColor(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
      'accent': 'var(--theme-accent-color)',
      'success': 'var(--theme-success-color)',
      'warning': 'var(--theme-warning-color)',
      'error': 'var(--theme-error-color)',
      'info': 'var(--theme-info-color)'
    };
    return themeColors[color] || color;
  }

  function getBackgroundStyle() {
    if (!config?.background) return '';
    
    const bg = config.background;
    
    if (bg.type === 'gradient' && bg.gradient) {
      const fromColor = getThemeColor(bg.gradient.from);
      const toColor = getThemeColor(bg.gradient.to);
      const direction = bg.gradient.direction || '135deg';
      return `background: linear-gradient(${direction}, ${fromColor}, ${toColor});`;
    }
    
    if (bg.type === 'color' && bg.value) {
      const color = getThemeColor(bg.value);
      return `background: ${color};`;
    }
    
    if (bg.type === 'image' && bg.image) {
      const imageUrl = bg.image.url || (bg.image.filename ? `/images/${bg.image.filename}` : '');
      if (!imageUrl) {
        throw new Error('Image background requires url or filename');
      }
      return `background-image: url('${imageUrl}'); background-size: cover; background-position: center;`;
    }
    
    return '';
  }

  onMount(() => {
    // Auto-skip if configured
    if (config?.autoSkip) {
      // Dispatch click immediately to trigger navigation
      setTimeout(() => {
        dispatch('click');
      }, 100);
      return;
    }

    // Auto-submit if configured
    if (config?.actions?.autoSubmit) {
      dispatch("autoSubmit");
    }

    // Get geolocation if configured to run on page load
    if (config?.actions?.getGoogleGeolocation?.onPageLoad) {
      dispatch("getGeolocation");
    }

    // Request fullscreen if configured
    if (config?.fullscreen) {
      dispatch("requestFullscreen");
    }

    // Add fade-in class after a frame to trigger transition
    requestAnimationFrame(() => {
      if (backgroundEl) {
        backgroundEl.classList.add('svelte-fade-in');
      }
    });
  });

  async function handleClick() {
    // Check if we need to get geolocation data when button is clicked
    if (config?.actions?.getGoogleGeolocation && !config?.actions?.getGoogleGeolocation?.onPageLoad) {
      dispatch("getGeolocation");
    }
    
    // Start fading out background
    if (backgroundEl) {
      backgroundEl.classList.remove('svelte-fade-in');
      backgroundEl.classList.add('svelte-fade-out');
    }

    await new Promise((resolve) => setTimeout(resolve, 250));
    // Start content fade
    showContent = false;

    // Wait for background fade out
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Finally set visible to false to remove from DOM
    visible = false;

    dispatch('click');
  }
</script>

<div class="screen-component" class:scrollable={config?.scrollable}>
  {#if visible}
    <div
      class="background-wrapper"
      bind:this={backgroundEl}
      style={getBackgroundStyle()}
      style:--bg-color={config?.background?.type === 'color' ? config.background.value : undefined}
      style:--dark-bg-color={config?.background?.type === 'color' && config.background.darkValue
        ? config.background.darkValue
        : undefined}
    >
      {#if config?.background?.type === 'image' && config.background.overlay}
        <div
          class="overlay"
          style:--overlay-color={config.background.overlay.color}
          style:--overlay-dark-color={config.background.overlay.darkColor}
          style:opacity={config.background.overlay.opacity}
        ></div>
      {/if}
    </div>
  {/if}

  {#if showContent}
    <div 
      class="content"
      style:--screen-text-color={config?.color}
    >
      <div
        class="image-container"
        class:hidden={!header?.image}
        out:fly={{ x: '-5%', duration: 200, delay: 0 }}
      >
        <div class="image-inner" use:slide={{ translate: 3, duration: 500, delay: 50, startOpacity: 0 }}>
          {#if header?.image}
            <Image
              src={header.image.url}
              alt={header.image.alt}
              aspectRatio={header.image.aspectRatio}
              svg={header.image.svg}
              scale={header.image.scale}
              animation={header.image.animation}
              backgroundSize="contain"
              sources={header.image.sources}
              originalSrc={header.image.originalSrc}
            />
          {/if}
        </div>
      </div>

      <div class="text-content">
        <div class="title-wrapper" out:fly={{ x: '-5%', duration: 200, delay: 50 }}>
          <div use:slide={{ translate: 9, duration: 400, delay: 150, startOpacity: 0 }}>
            <Title level="h1">{header?.title || ''}</Title>
          </div>
        </div>

        <div out:fly={{ x: '-4%', duration: 200, delay: 100 }}>
          <div use:slide={{ translate: 5, duration: 400, delay: 700, startOpacity: 0 }}>
            <Text>
              {header?.subtitle || ''}
            </Text>
          </div>
        </div>
      </div>

      <div class="blocks-container" out:fly={{ x: '-3%', duration: 200, delay: 10 }}>
        {#each blocks || [] as block, index}
          <div 
            use:slide={{ translate: 5, duration: 400, delay: 600 + (index * 120), startOpacity: 0 }}
            out:fly={{ x: '-3%', duration: 200, delay: index * 25 }}
          >
            <ScreenBlock {block} {index} onCtaClick={(action) => {
              if (action === 'startSurvey') {
                dispatch('click');
              } else if (action === 'submit') {
                dispatch('autoSubmit');
              } else if (action === 'getGeolocation') {
                dispatch('getGeolocation');
              }
            }} />
          </div>
        {/each}
      </div>

      {#if cta}
        <div
          use:slide={{ translate: 2, duration: 400, delay: 800, startOpacity: 0 }}
          out:fly={{ x: '-3%', duration: 200, delay: 150 }}
        >
          <Button label={cta.label} variant={cta.variant} size="md" on:click={handleClick} />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="postcss">
  .screen-component {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    justify-content: center;
    container-type: inline-size;
    --spacing-x: 1rem;
    padding: var(--spacing-x);
    box-sizing: border-box;
    position: relative;
    isolation: isolate;
  }

  .screen-component.scrollable {
    overflow-y: auto;
    justify-content: flex-start;
    align-items: center;
  }


  .background-wrapper {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-position: center bottom;
    background-size: cover;
    background-repeat: no-repeat;
    opacity: 0;
    transition: opacity 800ms ease-in-out;
  }

  .background-wrapper:global(.svelte-fade-in) {
    opacity: 1;
  }

  .background-wrapper:global(.svelte-fade-out) {
    opacity: 0;
    transition: opacity 450ms ease-in-out;
  }

  .overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: var(--overlay-color);
  }

  /* Dark mode support */
  :global([data-theme='dark']) .background-wrapper {
    background-color: var(--dark-bg-color, var(--bg-color-dark, #1a1a1a));
  }

  :global([data-theme='dark']) .overlay {
    background-color: var(--overlay-dark-color, var(--overlay-color));
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin: 0 auto;
    width: 100%;
    flex-shrink: 0;
    flex: 1;
    justify-content: center;
    position: relative;
    z-index: 2;
    --text-color: var(--screen-text-color);
    max-width: 600px;
  }

  .image-container {
    width: 100%;
    overflow: hidden;
    margin: 0 auto;
    flex-shrink: 0;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Content width constraints */
  @container (min-width: 600px) {
    .image-container,
    .content {
      max-width: 600px;
    }
  }

  .text-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .title-wrapper {
    isolation: isolate;
  }

  @container (max-width: 280px) {
    .screen-component {
      gap: 1rem;
      --spacing-x: 0.5rem;
    }

    .content {
      gap: 1rem;
    }

    .text-content {
      gap: 0.75rem;
    }

    :global(.title-container h2) {
      font-size: 1rem;
    }
  }

  .hidden {
    display: none;
  }
  .image-inner {
    width: 100%;
  }

  .blocks-container {
    width: 100%;
    max-width: 600px;
  }
</style>